import CloseIcon from '@mui/icons-material/Close'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid2'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useFormik } from 'formik'
import { has, map, remove } from 'lodash'
import { FC, Fragment } from 'react'
import { useParams } from 'react-router-dom'
import { useRequestsStore } from '../../../../store/storeRequests'
import { LLMModelSeries } from '../../../../swagger/dto'
import { SettingFormItemLabel } from './SettingFormItemLabel'
import { SettingsSlider } from './SettingsSlider'

export enum ModelParamType {
    RANGE = 'RANGE',
    BOOL = 'BOOL',
    SELECT_ONE = 'SELECT_ONE',
    SELECT_MULTIPLE = 'SELECT_MULTIPLE',
    TEXT = 'TEXT',
}

type SettingFormValues = LLMModelSeries['params'] | LLMModelSeries['additional_contexts']

type Props = {
    initialValues: SettingFormValues
    data: SettingFormValues[]
    nameParams: string
}

export const SettingForm: FC<Props> = ({ initialValues, data, nameParams }) => {
    const params = useParams()
    const {
        updateRequest,
        requestDetail,
        setRequestSettingAdditionalContexts,
        setRequestSettingParams,
        setIsDoneUpdateRequest,
    } = useRequestsStore((state) => state)

    const { values, handleSubmit, handleChange, handleBlur, isValid, dirty, setValues } = useFormik(
        {
            enableReinitialize: true,
            initialValues,
            onSubmit: async (values) => {
                const updateArray = map(data, (param) => {
                    return { name: param.name, value: values[param.name] }
                })
                if (params.requestId) {
                    await updateRequest(
                        { threadId: requestDetail?.id },
                        { [nameParams]: updateArray },
                    )
                } else {
                    if (nameParams === 'additional_contexts') {
                        setRequestSettingAdditionalContexts(updateArray)
                        setIsDoneUpdateRequest(true)
                    }
                    if (nameParams === 'params') {
                        setRequestSettingParams(updateArray)
                        setIsDoneUpdateRequest(true)
                    }
                }
            },
        },
    )

    const handleDeleteValue = (title, value) => {
        const buff = [...values[title]]
        remove(buff, (v) => v === value)
        setValues({ ...values, [title]: buff })
    }

    if (!data?.length) {
        return null
    }

    return (
        <form onSubmit={handleSubmit}>
            {map(data, (param) => {
                if (param?.param_type === ModelParamType.SELECT_ONE) {
                    return (
                        <Grid mb={2} key={param.name}>
                            <SettingFormItemLabel
                                title={param.title}
                                description={param.description}
                            />
                            <Select
                                key={param.name}
                                displayEmpty
                                MenuProps={{
                                    anchorOrigin: {
                                        vertical: 'top',
                                        horizontal: 'center',
                                    },
                                    transformOrigin: {
                                        vertical: 'top',
                                        horizontal: 'left',
                                    },
                                }}
                                sx={{ mr: 1, overflow: 'auto', width: '100%' }}
                                id={param.name}
                                name={param.name}
                                value={values[param.name]}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                color="secondary">
                                {map(param.values, (value, index) => {
                                    return (
                                        <MenuItem
                                            value={value}
                                            key={index}
                                            sx={{
                                                borderBottom: '1px solid #F4F4F6',
                                                p: '10px 16px',
                                            }}>
                                            <Grid
                                                container
                                                alignItems="center"
                                                sx={{ width: '100%', m: 0 }}>
                                                <Typography variant="h6">
                                                    {value === '' ? 'Не выбранно' : value}
                                                </Typography>
                                            </Grid>
                                        </MenuItem>
                                    )
                                })}
                            </Select>
                        </Grid>
                    )
                }
                if (
                    param?.param_type === ModelParamType.SELECT_MULTIPLE &&
                    has(values, param.name)
                ) {
                    return (
                        <Grid mb={2} key={param.name}>
                            <FormControl sx={{ m: 0, width: '100%' }}>
                                <SettingFormItemLabel
                                    title={param.title}
                                    description={param.description}
                                />

                                <Select
                                    key={param.name}
                                    renderValue={(selected) => selected.join(', ')}
                                    displayEmpty
                                    multiple
                                    MenuProps={{
                                        anchorOrigin: {
                                            vertical: 'top',
                                            horizontal: 'center',
                                        },
                                        transformOrigin: {
                                            vertical: 'top',
                                            horizontal: 'left',
                                        },
                                    }}
                                    sx={{ mr: 1, overflow: 'auto', mb: 2 }}
                                    id={param.name}
                                    name={param.name}
                                    value={values[param.name]}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    color="secondary">
                                    {map(param.values, (value, index) => {
                                        return (
                                            <MenuItem
                                                value={value}
                                                key={index}
                                                sx={{
                                                    borderBottom: '1px solid #F4F4F6',
                                                    p: '10px 16px',
                                                }}>
                                                <Grid
                                                    container
                                                    alignItems="center"
                                                    sx={{ width: '100%', m: 0 }}>
                                                    <Checkbox
                                                        checked={values[param.name]?.includes(
                                                            value,
                                                        )}
                                                    />
                                                    <Typography variant="h6">{value}</Typography>
                                                </Grid>
                                            </MenuItem>
                                        )
                                    })}
                                </Select>
                            </FormControl>

                            <Stack spacing={1} direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
                                {values[param.name]?.map((value) => (
                                    <Chip
                                        deleteIcon={
                                            <CloseIcon
                                                sx={{
                                                    '&.MuiChip-deleteIcon': {
                                                        fontSize: '12px',
                                                    },
                                                }}
                                            />
                                        }
                                        key={value}
                                        label={value}
                                        onDelete={() => handleDeleteValue(param.name, value)}
                                        color="primary"
                                    />
                                ))}
                            </Stack>
                        </Grid>
                    )
                }

                if (param?.param_type === ModelParamType.RANGE) {
                    return (
                        <Fragment key={param.name}>
                            <SettingFormItemLabel
                                title={param.title}
                                description={param.description}
                            />
                            <SettingsSlider
                                sx={{
                                    mb: 1,
                                }}
                                marks={[
                                    {
                                        value: param.min_value,
                                        label: param.min_value,
                                    },
                                    {
                                        value: param.max_value,
                                        label: param.max_value,
                                    },
                                ]}
                                id={param.name}
                                name={param.name}
                                value={values[param.name]}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                step={param.step}
                                min={param.min_value}
                                max={param.max_value}
                            />
                        </Fragment>
                    )
                }

                if (param?.param_type === ModelParamType.BOOL) {
                    return (
                        <Fragment key={param.name}>
                            <SettingFormItemLabel
                                title={param.title}
                                description={param.description}
                            />

                            <FormControlLabel
                                key={param.name}
                                id={param.name}
                                name={param.name}
                                value={values[param.name]}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                sx={{ mb: 1 }}
                                control={<Checkbox checked={values[param.name]} />}
                                label={param.title}
                            />
                        </Fragment>
                    )
                }
                if (param?.param_type === ModelParamType.TEXT) {
                    return (
                        <Fragment key={param.name}>
                            <SettingFormItemLabel
                                title={param.title}
                                description={param.description}
                            />
                            <TextField
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '24px',
                                    },
                                }}
                                multiline
                                rows={4}
                                key={param.name}
                                id={param.name}
                                name={param.name}
                                value={values[param.name]}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                margin="normal"
                                fullWidth
                            />
                        </Fragment>
                    )
                }
            })}
            <Button
                sx={{ mt: 4 }}
                fullWidth
                type="submit"
                disabled={!(isValid && dirty)}
                variant="contained">
                Сохранить
            </Button>
        </form>
    )
}
